import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Phone, Mail, Calculator, AlertCircle } from 'lucide-react';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';

export default function CalculatorPage() {
  const [formData, setFormData] = useState({
    squareFootage: '',
    bedrooms: 0,
    bathrooms: 0,
    kitchen: false,
    extraRooms: 0,
    stairs: 0,
    baseboards: 0,
    interiorWindows: 0,
    exteriorWindows: 0,
    insideFridge: false,
    insideOven: false,
    insideCabinets: false,
    pets: 0
  });

  const [totalPrice, setTotalPrice] = useState(0);
  const [priceBreakdown, setPriceBreakdown] = useState([]);
  const [contactInfo, setContactInfo] = useState({
    fullName: '',
    phone: '',
    email: '',
    address: '',
    description: ''
  });

  const calculatePrice = () => {
    const sqft = parseInt(formData.squareFootage) || 0;
    const isOver2500 = sqft > 2500;
    let price = 0;

    // Bedrooms
    if (formData.bedrooms > 0) {
      const bedroomPrice = isOver2500 ? 35 : 25;
      price += formData.bedrooms * bedroomPrice;
    }

    // Bathrooms
    if (formData.bathrooms > 0) {
      const bathroomPrice = isOver2500 ? 55 : 45;
      price += formData.bathrooms * bathroomPrice;
    }

    // Kitchen
    if (formData.kitchen) {
      const kitchenPrice = isOver2500 ? 60 : 45;
      price += kitchenPrice;
    }

    // Extra Rooms
    if (formData.extraRooms > 0) {
      const extraRoomPrice = isOver2500 ? 35 : 25;
      price += formData.extraRooms * extraRoomPrice;
    }

    // Stairs
    if (formData.stairs > 0) {
      price += formData.stairs * 20;
    }

    // Baseboards
    if (formData.baseboards > 0) {
      price += formData.baseboards * 10;
    }

    // Interior Windows
    if (formData.interiorWindows > 0) {
      price += formData.interiorWindows * 8;
    }

    // Exterior Windows
    if (formData.exteriorWindows > 0) {
      price += formData.exteriorWindows * 10;
    }

    // Inside Fridge
    if (formData.insideFridge) {
      price += 50;
    }

    // Inside Oven
    if (formData.insideOven) {
      price += 50;
    }

    // Inside Cabinets
    if (formData.insideCabinets) {
      const cabinetsPrice = isOver2500 ? 100 : 60;
      price += cabinetsPrice;
    }

    // Pets
    if (formData.pets > 0) {
      price += formData.pets * 10;
    }

    // Round to nearest dollar to avoid cents
    const finalPrice = Math.round(price);
    setTotalPrice(finalPrice);
    setPriceBreakdown(price > 0 ? [{ calculated: true }] : []);
  };

  useEffect(() => {
    calculatePrice();
    // eslint-disable-next-line
  }, [formData]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      
      // Determine if it's a full booking (with calculator) or just contact request
      const hasCalculatorData = priceBreakdown.length > 0;
      
      const response = await fetch(`${BACKEND_URL}/api/bookings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          contactInfo, 
          formData, 
          totalPrice: hasCalculatorData ? totalPrice : 0,
          isContactOnly: !hasCalculatorData
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        if (hasCalculatorData) {
          toast.success('Booking request submitted! We\'ll contact you shortly to confirm your appointment.');
        } else {
          toast.success('Quote request submitted! We\'ll reach out within 24 hours with a custom quote.');
        }
        
        // Reset form after successful submission
        setContactInfo({ fullName: '', phone: '', email: '', address: '', description: '' });
        setFormData({
          squareFootage: '',
          bedrooms: 0,
          bathrooms: 0,
          kitchen: false,
          extraRooms: 0,
          stairs: 0,
          baseboards: 0,
          interiorWindows: 0,
          exteriorWindows: 0,
          insideFridge: false,
          insideOven: false,
          insideCabinets: false,
          pets: 0
        });
      } else {
        toast.error('Submission failed. Please try again or call us directly.');
      }
    } catch (error) {
      console.error('Booking submission error:', error);
      toast.error('Submission failed. Please call or text (804) 583-7000 to book.');
    }
  };

  return (
    <div className="pt-24 pb-20 bg-gradient-to-b from-[#0B7CB5]/5 to-white">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <Calculator className="w-12 h-12 text-[#0B7CB5]" />
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900">
              Estimate <span className="text-[#0B7CB5]">Calculator</span>
            </h1>
          </div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Get a rough estimate for your cleaning service
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {/* Calculator Form */}
          <div className="lg:col-span-2 bg-white p-8 rounded-2xl shadow-lg">
            <div className="space-y-8">
              {/* Square Footage */}
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-[#0B7CB5]/20">
                  Property Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="squareFootage">Square Footage *</Label>
                    <Input
                      id="squareFootage"
                      type="number"
                      min="0"
                      value={formData.squareFootage}
                      onChange={(e) => handleChange('squareFootage', e.target.value)}
                      placeholder="e.g., 2000"
                      className="mt-1"
                    />
                    <p className="text-sm text-gray-500 mt-1">
                      {formData.squareFootage && parseInt(formData.squareFootage) > 2500
                        ? 'Over 2500 sq ft pricing applied'
                        : 'Under 2500 sq ft pricing applied'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Rooms */}
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-[#0B7CB5]/20">
                  Rooms
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="bedrooms">Number of Bedrooms</Label>
                    <Input
                      id="bedrooms"
                      type="number"
                      min="0"
                      value={formData.bedrooms === 0 ? '' : formData.bedrooms}
                      onChange={(e) => handleChange('bedrooms', parseInt(e.target.value) || 0)}
                      onBlur={(e) => !e.target.value && handleChange('bedrooms', 0)}
                      placeholder="0"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="bathrooms">Number of Bathrooms</Label>
                    <Input
                      id="bathrooms"
                      type="number"
                      min="0"
                      step="0.5"
                      value={formData.bathrooms === 0 ? '' : formData.bathrooms}
                      onChange={(e) => handleChange('bathrooms', parseFloat(e.target.value) || 0)}
                      onBlur={(e) => !e.target.value && handleChange('bathrooms', 0)}
                      placeholder="0"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="extraRooms">Extra Rooms (Living, Dining, Office, etc.)</Label>
                    <Input
                      id="extraRooms"
                      type="number"
                      min="0"
                      value={formData.extraRooms === 0 ? '' : formData.extraRooms}
                      onChange={(e) => handleChange('extraRooms', parseInt(e.target.value) || 0)}
                      onBlur={(e) => !e.target.value && handleChange('extraRooms', 0)}
                      placeholder="0"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="pets">Number of Pets *</Label>
                    <Input
                      id="pets"
                      type="number"
                      min="0"
                      value={formData.pets === 0 ? '' : formData.pets}
                      onChange={(e) => handleChange('pets', parseInt(e.target.value) || 0)}
                      onBlur={(e) => !e.target.value && handleChange('pets', 0)}
                      placeholder="0"
                      className="mt-1"
                    />
                  </div>
                  <div className="flex items-center space-x-2 pt-6">
                    <input
                      type="checkbox"
                      id="kitchen"
                      checked={formData.kitchen}
                      onChange={(e) => handleChange('kitchen', e.target.checked)}
                      className="w-5 h-5 text-[#0B7CB5] border-gray-300 rounded focus:ring-[#0B7CB5]"
                    />
                    <Label htmlFor="kitchen" className="cursor-pointer">Include Kitchen</Label>
                  </div>
                </div>
              </div>

              {/* Additional Services */}
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-[#0B7CB5]/20">
                  Additional Services
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="stairs">Flights of Stairs ($20 each)</Label>
                    <Input
                      id="stairs"
                      type="number"
                      min="0"
                      value={formData.stairs === 0 ? '' : formData.stairs}
                      onChange={(e) => handleChange('stairs', parseInt(e.target.value) || 0)}
                      onBlur={(e) => !e.target.value && handleChange('stairs', 0)}
                      placeholder="0"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="baseboards">Baseboard Rooms ($10 per room)</Label>
                    <Input
                      id="baseboards"
                      type="number"
                      min="0"
                      value={formData.baseboards === 0 ? '' : formData.baseboards}
                      onChange={(e) => handleChange('baseboards', parseInt(e.target.value) || 0)}
                      onBlur={(e) => !e.target.value && handleChange('baseboards', 0)}
                      placeholder="0"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="interiorWindows">Interior Windows ($8 each)</Label>
                    <Input
                      id="interiorWindows"
                      type="number"
                      min="0"
                      value={formData.interiorWindows === 0 ? '' : formData.interiorWindows}
                      onChange={(e) => handleChange('interiorWindows', parseInt(e.target.value) || 0)}
                      onBlur={(e) => !e.target.value && handleChange('interiorWindows', 0)}
                      placeholder="0"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="exteriorWindows">Exterior Windows ($10 each)</Label>
                    <Input
                      id="exteriorWindows"
                      type="number"
                      min="0"
                      value={formData.exteriorWindows === 0 ? '' : formData.exteriorWindows}
                      onChange={(e) => handleChange('exteriorWindows', parseInt(e.target.value) || 0)}
                      onBlur={(e) => !e.target.value && handleChange('exteriorWindows', 0)}
                      placeholder="0"
                      className="mt-1"
                    />
                  </div>
                </div>

                <div className="mt-6 space-y-3">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="insideFridge"
                      checked={formData.insideFridge}
                      onChange={(e) => handleChange('insideFridge', e.target.checked)}
                      className="w-5 h-5 text-[#0B7CB5] border-gray-300 rounded focus:ring-[#0B7CB5]"
                    />
                    <Label htmlFor="insideFridge" className="cursor-pointer">Inside Refrigerator ($50)</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="insideOven"
                      checked={formData.insideOven}
                      onChange={(e) => handleChange('insideOven', e.target.checked)}
                      className="w-5 h-5 text-[#0B7CB5] border-gray-300 rounded focus:ring-[#0B7CB5]"
                    />
                    <Label htmlFor="insideOven" className="cursor-pointer">Inside Oven ($50)</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="insideCabinets"
                      checked={formData.insideCabinets}
                      onChange={(e) => handleChange('insideCabinets', e.target.checked)}
                      className="w-5 h-5 text-[#0B7CB5] border-gray-300 rounded focus:ring-[#0B7CB5]"
                    />
                    <Label htmlFor="insideCabinets" className="cursor-pointer">
                      Inside Cabinets (${formData.squareFootage && parseInt(formData.squareFootage) > 2500 ? '100' : '60'})
                    </Label>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Information Section */}
            <div id="contact-form" className="scroll-mt-24">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-[#0B7CB5]/20">
                Your Contact Information
              </h3>
              <p className="text-gray-600 mb-4">
                <strong>For residential:</strong> Use calculator above, then fill out your contact info<br/>
                <strong>For commercial or custom quotes:</strong> Just fill out your contact info and we'll reach out!
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label htmlFor="fullName">Full Name *</Label>
                  <Input
                    id="fullName"
                    value={contactInfo.fullName}
                    onChange={(e) => setContactInfo(prev => ({ ...prev, fullName: e.target.value }))}
                    className="mt-1"
                    placeholder="Your full name"
                    autoComplete="new-password"
                    data-lpignore="true"
                    data-form-type="other"
                  />
                </div>
                <div>
                  <Label htmlFor="contactPhone">Phone Number *</Label>
                  <Input
                    id="contactPhone"
                    type="tel"
                    value={contactInfo.phone}
                    onChange={(e) => setContactInfo(prev => ({ ...prev, phone: e.target.value }))}
                    className="mt-1"
                    placeholder="(804) 555-0123"
                    autoComplete="new-password"
                    data-lpignore="true"
                  />
                </div>
                <div>
                  <Label htmlFor="contactEmail">Email Address *</Label>
                  <Input
                    id="contactEmail"
                    type="email"
                    value={contactInfo.email}
                    onChange={(e) => setContactInfo(prev => ({ ...prev, email: e.target.value }))}
                    className="mt-1"
                    placeholder="your@email.com"
                    autoComplete="new-password"
                    data-lpignore="true"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="address">Property Address *</Label>
                  <Input
                    id="address"
                    value={contactInfo.address}
                    onChange={(e) => setContactInfo(prev => ({ ...prev, address: e.target.value }))}
                    className="mt-1"
                    placeholder="123 Main St, Richmond, VA 23219"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="description">Service Description (Optional)</Label>
                  <Textarea
                    id="description"
                    value={contactInfo.description}
                    onChange={(e) => setContactInfo(prev => ({ ...prev, description: e.target.value }))}
                    className="mt-1"
                    rows={4}
                    placeholder="For commercial clients or special cases: Please describe your cleaning needs, property type, or any special requirements..."
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Price Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white p-8 rounded-2xl shadow-lg sticky top-24">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                {priceBreakdown.length > 0 ? 'Estimated Price' : 'Contact Us'}
              </h3>
              
              {priceBreakdown.length > 0 ? (
                <div className="mb-6">
                  <div className="bg-[#0B7CB5]/10 p-6 rounded-xl mb-4">
                    <p className="text-sm text-gray-600 mb-2 text-center">Rough Estimate:</p>
                    <div className="text-5xl font-bold text-[#0B7CB5] text-center">
                      ${totalPrice}
                    </div>
                  </div>
                  <div className="bg-red-50 border-2 border-red-300 p-4 rounded-lg mb-4">
                    <p className="text-red-800 font-bold text-sm text-center mb-2">
                      ⚠️ THIS IS NOT YOUR FINAL PRICE
                    </p>
                    <p className="text-red-700 text-xs text-center">
                      This is only a rough estimate. Your actual price will be determined during your consultation.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="mb-6">
                  <div className="bg-blue-50 border-2 border-blue-300 p-4 rounded-lg">
                    <p className="text-blue-800 text-sm text-center">
                      Perfect for commercial properties, move-outs, or custom cleaning needs!
                    </p>
                  </div>
                </div>
              )}

              <div className="space-y-3 mb-6">
                <Button
                  onClick={handleSubmit}
                  size="lg"
                  className="w-full bg-[#0B7CB5] hover:bg-[#095a87] text-white font-semibold"
                  disabled={!contactInfo.fullName || !contactInfo.phone || !contactInfo.email || !contactInfo.address}
                >
                  {priceBreakdown.length > 0 ? 'Book Now' : 'Request Quote'}
                </Button>
                <div className="text-center text-sm text-gray-500">or</div>
                <a 
                  href="tel:+18045837000"
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium transition-colors h-10 w-full border-2 border-[#0B7CB5] text-[#0B7CB5] hover:bg-[#0B7CB5] hover:text-white px-4 py-2"
                  style={{ touchAction: 'manipulation', cursor: 'pointer', WebkitTapHighlightColor: 'rgba(11, 124, 181, 0.3)', zIndex: 50 }}
                >
                  <Phone className="mr-2 w-5 h-5" />
                  Call or Text (804) 583-7000
                </a>
              </div>

              <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg">
                <div className="flex items-start space-x-2">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-red-800 space-y-2">
                    <p className="font-bold">IMPORTANT - READ CAREFULLY:</p>
                    <ul className="list-disc list-inside space-y-1 text-xs">
                      <li className="font-bold">This is NOT your final price - only a rough estimate</li>
                      <li>Actual pricing determined after initial consultation</li>
                      <li>All clients must have an initial first-time deep cleaning</li>
                      <li>Monthly rates determined after first visit</li>
                      <li>Estimates based on normal condition homes</li>
                      <li className="font-bold">You must call or text <a href="tel:+18045837000" className="text-[#0B7CB5] hover:underline">(804) 583-7000</a> or <a href="mailto:custommaidsrva@gmail.com" className="text-[#0B7CB5] hover:underline">email</a> to confirm booking</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}